import formatLocaleNumber from "~/helpers/localeNumberFormat";
import i18n from "~/i18n/i18n";

export interface LineUnitTotals {
	lineCount: number;
	unitCount: number;
}

export const aggregateLineUnitTotals = <TLine>(
	lines: readonly TLine[],
	unitsOf: (line: TLine) => number
): LineUnitTotals =>
	lines.reduce<LineUnitTotals>(
		(totals, line) => ({
			lineCount: totals.lineCount + 1,
			unitCount: totals.unitCount + unitsOf(line)
		}),
		{ lineCount: 0, unitCount: 0 }
	);

export type UnitNoun = "each" | "unit";

export const UNIT_NOUN: UnitNoun = "unit";

export const EACHES_NOUN: UnitNoun = "each";

const formatQuantity = (value: number): string => formatLocaleNumber(value);

export const pluralize = (count: number, noun: string): string => (count === 1 ? noun : `${noun}s`);

const NOUN_KEYS: Record<UnitNoun, string> = {
	each: "common:grid.orderSummary.eaches",
	unit: "common:grid.orderSummary.units"
};

const formatUnitCount = (count: number, noun: UnitNoun): string =>
	i18n.t(NOUN_KEYS[noun], { count, qty: formatQuantity(count) });

const joinSummaryParts = (left: string, right: string): string =>
	i18n.t("common:grid.orderSummary.joined", { left, right });

export const formatLineUnitSummary = (
	{ lineCount, unitCount }: LineUnitTotals,
	unitNoun: UnitNoun = UNIT_NOUN
): string =>
	joinSummaryParts(
		i18n.t("common:grid.orderSummary.lines", { count: lineCount, qty: formatQuantity(lineCount) }),
		formatUnitCount(unitCount, unitNoun)
	);

export const formatLineEachesAndUnitsSummary = (totals: LineUnitTotals, unitTotal: null | number): string => {
	const eaches = formatLineUnitSummary(totals, EACHES_NOUN);
	return unitTotal == null || unitTotal === totals.unitCount
		? eaches
		: joinSummaryParts(eaches, formatUnitCount(unitTotal, UNIT_NOUN));
};
